import { type InputHTMLAttributes, forwardRef, useEffect, useState } from 'react'

import { cn } from '@elsapiens/utils'

export interface PatternInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'pattern'> {
  value?: string
  onChange?: (value: string) => void
  pattern: RegExp
  formatHint: string
  formatExample?: string
  transform?: 'uppercase' | 'lowercase' | 'none'
  error?: string
  helperText?: string
  hideMessage?: boolean
  showFormatHint?: boolean
  validationThreshold?: number
}

function buildFormatError(formatHint: string, formatExample?: string): string {
  const parts = [`Expected format: ${formatHint}`]
  if (formatExample) { parts.push(`(e.g., ${formatExample})`) }
  return parts.join(' ')
}

interface UseValidationInput {
  value: string
  pattern: RegExp
  formatHint: string
  formatExample?: string
  validationThreshold: number
}

function useValidationError(input: UseValidationInput): string {
  const [validationError, setValidationError] = useState<string>('')
  const { value, pattern, formatHint, formatExample, validationThreshold } = input
  useEffect(() => {
    if (!value || !pattern) {
      setValidationError('')
      return
    }
    try {
      const isValid = pattern.test(value)
      if (!isValid && value.length >= validationThreshold) {
        setValidationError(buildFormatError(formatHint, formatExample))
      } else {
        setValidationError('')
      }
    /* v8 ignore next 3 */
    } catch {
      setValidationError('')
    }
  }, [value, pattern, formatHint, formatExample, validationThreshold])
  return validationError
}

function applyTransform(value: string, transform: 'uppercase' | 'lowercase' | 'none'): string {
  if (transform === 'uppercase') { return value.toUpperCase() }
  if (transform === 'lowercase') { return value.toLowerCase() }
  return value
}

interface MessageProps {
  hideMessage: boolean
  displayError: string
  helperText?: string
}

function FieldMessage({ hideMessage, displayError, helperText }: MessageProps): JSX.Element | null {
  if (hideMessage || (!displayError && !helperText)) { return null }
  return (
    <div className="el-field-message">
      {displayError && <p className="el-field-error">{displayError}</p>}
      {!displayError && helperText && <p className="el-field-helper">{helperText}</p>}
    </div>
  )
}

export const PatternInput = forwardRef<HTMLInputElement, PatternInputProps>(
  (props, ref) => {
    const {
      value = '', onChange, pattern, formatHint, formatExample, transform = 'none',
      error, helperText, hideMessage: shouldHideMessage = false, showFormatHint: shouldShowFormatHint = true,
      validationThreshold = 3, className, disabled: isDisabled, placeholder, maxLength, ...rest
    } = props

    const validationError = useValidationError({ value, pattern, formatHint, formatExample, validationThreshold })

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      onChange?.(applyTransform(e.target.value, transform))
    }

    /* v8 ignore next */
    const effectivePlaceholder = placeholder ?? formatExample ?? formatHint ?? 'Enter value'
    const hasError = !!error || (shouldShowFormatHint && !!validationError)
    const displayError = error ?? (shouldShowFormatHint ? validationError : '')

    return (
      <div className="w-full">
        <input
          ref={ref}
          className={cn('el-input el-input-md', hasError && 'el-input-invalid', className)}
          disabled={isDisabled}
          maxLength={maxLength}
          placeholder={effectivePlaceholder}
          type="text"
          value={value}
          onChange={handleChange}
          {...rest}
        />
        <FieldMessage displayError={displayError} helperText={helperText} hideMessage={shouldHideMessage} />
      </div>
    )
  }
)

PatternInput.displayName = 'PatternInput'
